
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EpisodeInsert } from "../types";

export const useEpisodeOperations = () => {
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const saveEpisode = async (episode: EpisodeInsert, editingEpisodeId?: string) => {
    if (saving) {
      console.log("Save already in progress, ignoring duplicate call");
      return;
    }

    try {
      setSaving(true);
      console.log("Starting save episode:", { episode, editingEpisodeId });
      
      // Basic validation
      if (!episode.title?.trim()) {
        throw new Error("Title is required");
      }
      
      if (!episode.category_id?.trim()) {
        throw new Error("Category is required");
      }

      // Prepare episode data
      const episodeData = {
        title: episode.title.trim(),
        description: episode.description?.trim() || null,
        thumbnail_url: episode.thumbnail_url?.trim() || null,
        duration: episode.duration?.trim() || null,
        youtube_id: episode.youtube_id?.trim() || null,
        vimeo_id: episode.vimeo_id?.trim() || null,
        category_id: episode.category_id,
        status: episode.status || 'draft',
        featured: episode.featured || false,
        video_platform: episode.video_platform || null,
      };
      
      console.log("Episode data to save:", episodeData);
      
      if (editingEpisodeId) {
        // Update existing episode
        console.log("Updating episode with ID:", editingEpisodeId);
        const { data, error } = await supabase
          .from("episodes")
          .update(episodeData)
          .eq("id", editingEpisodeId)
          .select();

        if (error) {
          console.error("Supabase update error:", error);
          throw new Error(`Failed to update episode: ${error.message}`);
        }
        
        console.log("Episode updated successfully:", data);
        toast({ title: "Success", description: "Episode updated successfully!" });
      } else {
        // Create new episode
        console.log("Creating new episode:", episodeData);
        
        const { data, error } = await supabase
          .from("episodes")
          .insert([episodeData])
          .select();

        if (error) {
          console.error("Supabase insert error:", error);
          throw new Error(`Failed to create episode: ${error.message}`);
        }
        
        if (!data || data.length === 0) {
          console.error("No data returned from insert operation");
          throw new Error("Episode was not created - no data returned");
        }
        
        console.log("Episode created successfully:", data);
        toast({ title: "Success", description: "Episode created successfully!" });
      }
    } catch (error: any) {
      console.error("Save episode error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save episode",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
      console.log("Save episode operation completed");
    }
  };

  const deleteEpisode = async (episodeId: string) => {
    try {
      console.log("Deleting episode:", episodeId);
      
      const { error } = await supabase
        .from("episodes")
        .delete()
        .eq("id", episodeId);

      if (error) {
        console.error("Error deleting episode:", error);
        throw error;
      }

      console.log("Episode deleted successfully");
      toast({ title: "Success", description: "Episode deleted successfully!" });
    } catch (error: any) {
      console.error("Delete episode error:", error);
      toast({
        title: "Error",
        description: "Failed to delete episode: " + error.message,
        variant: "destructive",
      });
    }
  };

  return {
    saving,
    saveEpisode,
    deleteEpisode,
  };
};
